/**
 * Verre Jaune — Jaune Fire (storefront)
 * --------------------------------------------------------------
 * - Registers the <fire-vote> custom element used on product cards.
 * - Batches every card on the page into ONE /levels request.
 * - Optimistic UI: the flame grows the instant you click, then
 *   reconciles with the server.
 * - Threshold gating: a design's flame stays dormant and its count
 *   hidden until it crosses the server-set threshold. Low numbers are
 *   never on screen — you see either "not yet" or an earned figure.
 *   (Clicking a dormant flame still bursts, so it always feels alive.)
 * - Tiered caps: signed-out visitors hit a low cap; once maxed, the
 *   button becomes a contextual "sign in to burn brighter" prompt that
 *   links to account creation. Signed-in customers get a higher cap.
 * - Flame size scales with the global level (log scale, capped).
 * - Optional Cloudflare Turnstile (invisible) before the first vote.
 * - Fills the "Hall of Flame" block if present.
 *
 * Loaded globally from layout/theme.liquid via patch-layout-jaune-fire.js:
 *   <script src="{{ 'vj-fire-vote.js' | asset_url }}" defer></script>
 */
(function () {
  'use strict';

  var SID_KEY = 'vj_fire_sid';
  var MAXED_KEY = 'vj_fire_maxed';        // { [productId]: { cap, member } }
  var RESET_V_KEY = 'vj_fire_reset_v';    // last server reset_version this browser saw
  var REGISTER_URL = '/account/register'; // where the guest sign-in pitch leads
  var DEFAULT_THRESHOLD = 10;

  // ---- tiny storage helpers (fail silently in private mode) ----
  function getStore(key) { try { return JSON.parse(localStorage.getItem(key)) || null; } catch (e) { return null; } }
  function setStore(key, val) { try { localStorage.setItem(key, JSON.stringify(val)); } catch (e) {} }
  function getMaxedMap() { return getStore(MAXED_KEY) || {}; }
  function rememberMaxed(id, cap, member) {
    var m = getMaxedMap();
    m[String(id)] = { cap: cap, member: !!member };
    setStore(MAXED_KEY, m);
  }
  function forgetMaxed(id) {
    var m = getMaxedMap();
    if (m[String(id)]) { delete m[String(id)]; setStore(MAXED_KEY, m); }
  }
  // Server-side WIPE-ALL bumps reset_version. When the version the server
  // reports is newer than the one this browser last saw, every prior maxed
  // state is meaningless (the counts are gone) — drop the whole map and bump
  // our stored marker. Returns true when a reset was detected this batch.
  function consumeResetVersion(serverV) {
    if (typeof serverV !== 'number' || !isFinite(serverV)) return false;
    var stored = parseInt(getStore(RESET_V_KEY), 10);
    if (!isFinite(stored)) stored = 0;
    if (serverV > stored) {
      setStore(MAXED_KEY, {});
      setStore(RESET_V_KEY, serverV);
      return true;
    }
    return false;
  }

  // ---- display helpers ----
  function formatCount(n) {
    if (n < 1000) return String(n);
    if (n < 100000) return (n / 1000).toFixed(n < 10000 ? 1 : 0).replace('.0', '') + 'k';
    return Math.round(n / 1000) + 'k';
  }
  // Flame scale curve — switched from log10 to sqrt (2026-05-14 follow-up).
  // The log10 tune (k=0.42) gave too little distinction between realistic
  // levels (25 / 80 / 180 read at 1.59 / 1.81 / 1.96 — only ~0.2 delta in the
  // low-to-mid range that fans actually see). sqrt(level) * 0.0707 gives
  // visibly distinct steps in the same range — 1.35 / 1.63 / 1.95 — at the
  // cost of an earlier cap (engages around level 200). The cap is also
  // lowered (2.3 → 2.0) so the flame stays contained inside .vj-bento__tile
  // and .vj-strip__card-img (both overflow:hidden) at the burst peak. The
  // "viral level keeps growing" payoff is deferred to a future "breakout
  // flame" concept (see PROJECT_STATE.md §20.3) — that's surface-specific
  // and needs its own design pass.
  function flameScale(level) {
    if (!(level > 0)) return '1.000';
    var s = 1 + Math.sqrt(level) * 0.0707;
    return Math.min(s, 2.0).toFixed(3);
  }

  // ---- Turnstile (optional, invisible) ----
  var turnstilePromise = null;
  function loadTurnstile() {
    if (turnstilePromise) return turnstilePromise;
    turnstilePromise = new Promise(function (resolve) {
      var s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      s.async = true; s.defer = true;
      s.onload = function () { resolve(window.turnstile); };
      s.onerror = function () { resolve(null); };
      document.head.appendChild(s);
    });
    return turnstilePromise;
  }
  function getTurnstileToken(sitekey) {
    return loadTurnstile().then(function (ts) {
      if (!ts) return null;
      return new Promise(function (resolve) {
        var holder = document.createElement('div');
        holder.style.display = 'none';
        document.body.appendChild(holder);
        ts.render(holder, {
          sitekey: sitekey,
          size: 'invisible',
          callback: function (token) { resolve(token); },
          'error-callback': function () { resolve(null); },
        });
        ts.execute(holder);
      });
    });
  }

  // ---- batch loader: collect all cards, fetch levels + champion badges in
  //                   two parallel calls (one batch per page, not per card) ----
  var registry = [];
  var batchTimer = null;
  function scheduleBatch() {
    if (batchTimer) return;
    batchTimer = setTimeout(runBatch, 60);
  }
  function runBatch() {
    batchTimer = null;
    var pending = registry.splice(0);
    if (!pending.length) return;
    var proxy = pending[0].el.dataset.proxy || '/apps/fire';
    var ids = pending.map(function (p) { return p.id; }).join(',');

    var levelsP = fetch(proxy + '/levels?ids=' + encodeURIComponent(ids), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : { levels: {} }; })
      .catch(function () { return { levels: {} }; });

    // Champions endpoint is 1h-cacheable server-side; storefront still hits
    // it on every page load (same shape as /levels) but the response comes
    // out of Shopify's CDN cache for repeat visitors. `v=2` versions the
    // cache key: v1 responses predate the design-aware sibling expansion
    // (and some browsers/edges held pre-ceremony empties for 24h — the
    // 2026-07-14 FR-only missing-medal report).
    var championsP = fetch(proxy + '/champions?v=2&product_ids=' + encodeURIComponent(ids), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : { champions: {} }; })
      .catch(function () { return { champions: {} }; });

    Promise.all([levelsP, championsP]).then(function (results) {
      var data = results[0] || {};
      var levels = data.levels || {};
      var threshold = typeof data.threshold === 'number' ? data.threshold : DEFAULT_THRESHOLD;
      var champs = (results[1] && results[1].champions) || {};
      // Structural WIPE-ALL recovery: if the server's reset_version is newer
      // than what this browser last saw, the maxed map was just cleared —
      // strip the maxed UI from every card on the page before re-applying
      // levels so a wiped design doesn't stay frozen at its old cap.
      var wasReset = consumeResetVersion(data.reset_version);
      pending.forEach(function (p) {
        if (wasReset) p.el.clearMaxed();
        p.el.threshold = threshold;
        p.el.applyLevel(levels[p.id] || 0, false);
        var pc = champs[String(p.id)];
        if (pc && pc.length) p.el.applyChampions(pc);
      });
    });
  }

  // ---- the custom element ----
  function defineFireVote() {
    if (window.customElements.get('fire-vote')) return;
    window.customElements.define('fire-vote', class extends HTMLElement {
      connectedCallback() {
        this.productId = this.dataset.productId;
        this.proxy = this.dataset.proxy || '/apps/fire';
        this.loggedIn = this.dataset.loggedIn === 'true';
        this.btn = this.querySelector('.vj-fire__btn');
        this.flame = this.querySelector('.vj-fire__flame');
        this.countEl = this.querySelector('[data-fire-count]');
        this.tipEl = this.querySelector('[data-fire-tip]');
        this.threshold = DEFAULT_THRESHOLD;
        this.level = 0;
        this.sending = 0; // optimistic clicks not yet confirmed
        this._origAria = this.btn.getAttribute('aria-label');

        // Restore a maxed state this browser already reached — but only while it
        // still matches the current identity TIER. A guest who maxed the low
        // guest cap (member:false) and has since signed in is no longer maxed:
        // the member cap is higher and is counted under a separate cust:
        // identity server-side, so the stale guest-max would wrongly freeze the
        // flame at 5. If the stored tier and the current signed-in state
        // disagree (guest→member on login, or member→guest on logout), forget
        // it and let the server re-establish the real cap on the next vote.
        var prior = getMaxedMap()[String(this.productId)];
        if (prior) {
          if (!!prior.member !== !!this.loggedIn) {
            forgetMaxed(this.productId);
          } else {
            this.applyMaxed(prior.cap, prior.member, false);
          }
        }

        // Bind the click handler ONCE across reconnects (QA 2026-07-21 fix).
        // connectedCallback fires every time the element is inserted into the
        // DOM — a product card, slider, or the PDP gallery re-parenting the
        // flame all count — so a plain addEventListener stacked a SECOND click
        // listener each time, and from then on one physical click ran onClick
        // twice → two POST /vote → the flame jumped by two. Keep a single bound
        // reference and remove-then-add so the listener is idempotent no matter
        // how often the element reconnects; disconnectedCallback tears it down.
        if (!this._onClickBound) this._onClickBound = this.onClick.bind(this);
        this.btn.removeEventListener('click', this._onClickBound);
        this.btn.addEventListener('click', this._onClickBound);
        registry.push({ el: this, id: this.productId });
        scheduleBatch();
      }

      disconnectedCallback() {
        // Detach the click listener when the element leaves the DOM so it can't
        // survive on a re-parented node (paired with the idempotent bind above).
        if (this.btn && this._onClickBound) {
          this.btn.removeEventListener('click', this._onClickBound);
        }
      }

      // Render a level. Below the threshold the flame stays dormant and the
      // count stays hidden — but `burst` still fires so a click always lands.
      applyLevel(level, burst) {
        this.level = Math.max(0, level | 0);
        // Immediate WIPE-ALL / single-reset recovery: a browser that hit its
        // per-design cap only got there by adding `cap` to this design's
        // global level, so a maxed design can never legitimately report a
        // level below that cap. If it does, the server count was zeroed
        // (admin reset or wipe) — drop the stale maxed state so the flame
        // becomes tappable again. The reset_version path in runBatch is the
        // structural fix; this is the belt-and-braces immediate one.
        if (this.classList.contains('is-maxed')) {
          var entry = getMaxedMap()[String(this.productId)];
          var cap = entry && typeof entry.cap === 'number' ? entry.cap : null;
          if (cap != null && this.level < cap) this.clearMaxed();
        }
        var lit = this.level >= this.threshold;
        this.classList.toggle('is-lit', lit);
        this.countEl.textContent = lit ? formatCount(this.level) : '';
        this.flame.style.setProperty('--vj-fire-scale', flameScale(lit ? this.level : 0));
        if (burst) {
          this.classList.remove('is-bursting');
          void this.offsetWidth; // restart animation
          this.classList.add('is-bursting');
        }
      }

      // Render the season-champion badge. The endpoint returns an array of
      // { season_number, final_rank } so a multi-season champion can show
      // multiple. We render the most recent one as a bottom-left corner chip
      // on the card media (the free corner: flame TL, save TR, quick-add BR);
      // the others remain in DOM dataset for a future "hover for full
      // history" treatment.
      //
      // Anchoring (2026-07-14 rework): <product-card> is the positioned
      // ancestor for absolute children (see vj-save.css), so bottom:12px
      // would land beside the price text, not the artwork. The chip is
      // appended to the fire anchor's PARENT (.card-gallery on cards,
      // .vj-gallery-main on the PDP) which gets `vj-champion-host`
      // (position:relative) so `bottom` resolves against the media box.
      applyChampions(list) {
        if (!Array.isArray(list) || !list.length) return;
        // Pick most recent (highest season_number).
        var top = list.slice().sort(function (a, b) { return (b.season_number||0) - (a.season_number||0); })[0];
        if (!top) return;
        var anchor = this.closest('.vj-fire-card-anchor');
        var host = anchor ? anchor.parentElement : null;
        var isPdp = !!(anchor && anchor.classList.contains('vj-fire-card-anchor--pdp-gallery'));
        // Avoid duplicate chips on re-batch.
        if ((host || this).querySelector('.vj-fire__champion')) return;
        var isFr = (document.documentElement.lang || '').toLowerCase().indexOf('fr') === 0;
        // Build the chip as a small <a> linking to /pages/the-fire. The
        // locale prefix must be explicit — client-side navigation gets no
        // server-side locale rewrite (same fix as the-fire page CTAs).
        // The chip carries a textual tooltip that reveals on hover/focus +
        // a touch-friendly press-to-show variant for mobile.
        var pill = document.createElement('a');
        pill.className = 'vj-fire__champion vj-fire__champion--rank' + top.final_rank
          + (isPdp ? ' vj-fire__champion--pdp' : '')
          + (host ? '' : ' vj-fire__champion--inline');
        // Prefix from Shopify.routes.root ('/' EN, '/fr/' FR) — the shape
        // already changed once (/fr-ca → /fr, Markets migration 2026-07-20).
        pill.href = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/$/, '') + '/pages/the-fire';
        var tooltipText = isFr
          ? 'Champion de la Saison ' + top.season_number + ', choisi par les fans dans le top 3. Pour en savoir plus →'
          : 'Season ' + top.season_number + ' Champion, voted top 3 by fans. Learn more →';
        pill.setAttribute('aria-label', tooltipText);
        // Native tooltip fallback for browsers that ignore the custom one.
        pill.title = tooltipText;
        // Stop the card's wrapping <a> overlay from swallowing the click.
        pill.addEventListener('click', function (e) { e.stopPropagation(); });
        // Touch: first tap reveals the tooltip; second tap follows the link.
        // Desktop hover/focus handles itself via CSS.
        var revealedAt = 0;
        pill.addEventListener('touchstart', function (ev) {
          if (!pill.classList.contains('is-revealed')) {
            ev.preventDefault();
            pill.classList.add('is-revealed');
            revealedAt = Date.now();
            // Auto-hide after 3.5s if the user moves on.
            setTimeout(function () { pill.classList.remove('is-revealed'); }, 3500);
          }
        }, { passive: false });
        // The visible content: flat brand medal SVG (ribbon tails + metal
        // disc + ink ring — tinted by the rank class via --vj-medal) plus
        // "S<n>". Replaces the raw OS medal emoji, which rendered
        // inconsistently across platforms and off the brand style. Tooltip
        // text rendered as a sibling span so it can be positioned absolutely.
        var lbl = document.createElement('span');
        lbl.className = 'vj-fire__champion-label';
        lbl.innerHTML = MEDAL_SVG;
        var txt = document.createElement('span');
        txt.textContent = 'S' + top.season_number;
        lbl.appendChild(txt);
        var tip = document.createElement('span');
        tip.className = 'vj-fire__champion-tip';
        tip.textContent = tooltipText;
        pill.appendChild(lbl);
        pill.appendChild(tip);
        if (host) {
          host.classList.add('vj-champion-host');
          host.appendChild(pill);
          // Subtle rank-tinted contour on the card media — cards only; the
          // PDP gallery frame stays unringed (owner call, 2026-07-14).
          if (!isPdp) {
            host.classList.add('vj-card--champion', 'vj-card--champion--rank' + top.final_rank);
          }
        } else {
          // Legacy fallback: no anchor wrapper — inline next to the flame.
          this.appendChild(pill);
        }
        this.classList.add('vj-fire--has-champion');
      }

      // Move the button into its maxed state. `fresh` = just hit the cap now
      // (vs. restored from storage), which briefly reveals the tip.
      applyMaxed(cap, isMember, fresh) {
        this.classList.add('is-maxed');
        var isFr = (document.documentElement.lang || '').toLowerCase().indexOf('fr') === 0;
        // Treat current logged-in status as the source of truth for the pitch.
        var memberNow = this.loggedIn;
        if (memberNow) {
          this.tipEl.textContent = isFr
            ? 'Tu as attisé tes ' + cap + ' flammes ici 🔥'
            : 'You’ve maxed your ' + cap + ' flames here 🔥';
          this.classList.remove('is-maxed--guest');
        } else {
          this.tipEl.textContent = isFr
            ? 'Tu as atteint tes ' + cap + ' flammes. Connecte-toi pour attiser plus fort →'
            : 'That’s all ' + cap + '. Sign in to burn brighter →';
          this.classList.add('is-maxed--guest');
        }
        this.tipEl.hidden = false;
        this.btn.setAttribute('aria-label', this.tipEl.textContent);
        if (fresh) {
          this.classList.add('is-revealing-tip');
          var self = this;
          setTimeout(function () { self.classList.remove('is-revealing-tip'); }, 3500);
        }
      }

      // Tear the maxed UI back down to a normal tappable flame and forget the
      // stored cap for this product. Called when a server reset (single or
      // WIPE-ALL) makes a previously-maxed state stale.
      clearMaxed() {
        if (!this.classList.contains('is-maxed') &&
            !getMaxedMap()[String(this.productId)]) return;
        forgetMaxed(this.productId);
        this.classList.remove('is-maxed', 'is-maxed--guest', 'is-revealing-tip', 'is-nudge');
        if (this.tipEl) {
          this.tipEl.hidden = true;
          this.tipEl.textContent = '';
        }
        if (this._origAria != null) {
          this.btn.setAttribute('aria-label', this._origAria);
        }
      }

      onClick(e) {
        // Product cards are wrapped in <a> — never let the click navigate.
        e.preventDefault();
        e.stopPropagation();

        if (this.classList.contains('is-maxed')) {
          if (!this.loggedIn) {
            // Caught in the act of caring — send them to create an account.
            // Locale prefix must be explicit (same fix as the champion chip):
            // a bare /account/register enters the hosted account flow in EN
            // even from FR pages. Prefix from Shopify.routes.root, never
            // hardcoded (/fr-ca → /fr, Markets migration 2026-07-20).
            window.location.href = ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/').replace(/\/$/, '') + REGISTER_URL;
            return;
          }
          // Member already maxed — just a friendly nudge.
          this.classList.remove('is-nudge');
          void this.offsetWidth;
          this.classList.add('is-nudge');
          return;
        }

        // Optimistic: grow now, confirm later.
        this.sending++;
        this.applyLevel(this.level + 1, true);
        this.sendVote();
      }

      sendVote() {
        var self = this;
        var sitekey = this.dataset.turnstileSitekey;
        var tokenP = sitekey ? getTurnstileToken(sitekey) : Promise.resolve(null);

        tokenP.then(function (token) {
          return fetch(self.proxy + '/vote', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              product_id: self.productId,
              handle: self.dataset.productHandle,
              title: self.dataset.productTitle,
              session: getStore(SID_KEY),
              turnstile_token: token,
            }),
          });
        })
        .then(function (r) {
          if (r.status === 429 || r.status === 403 || r.status === 401) {
            // Rejected by an anti-abuse layer — roll the optimistic click back.
            self.sending = Math.max(0, self.sending - 1);
            self.applyLevel(Math.max(0, self.level - 1), false);
            self.classList.add('is-cooling');
            setTimeout(function () { self.classList.remove('is-cooling'); }, 1200);
            return null;
          }
          return r.ok ? r.json() : null;
        })
        .then(function (data) {
          if (!data) return;
          self.sending = Math.max(0, self.sending - 1);
          if (data.session) setStore(SID_KEY, data.session);
          if (typeof data.threshold === 'number') self.threshold = data.threshold;
          // Trust the server's number once nothing else is in flight.
          if (self.sending === 0 && typeof data.fire_level === 'number') {
            self.applyLevel(data.fire_level, false);
          }
          if (data.maxed) {
            rememberMaxed(self.productId, data.cap, data.is_member);
            self.applyMaxed(data.cap, data.is_member, true);
          }
        })
        .catch(function () {
          // Network error — keep the optimistic value, it resyncs on reload.
          self.sending = Math.max(0, self.sending - 1);
        });
      }
    });
  }

  // ---- Hall of Flame (top-3 podium, Wave 2 rebuild) ----
  // Reads /apps/fire/podium. Source = 'season_podium' once a snapshot has run
  // (each row carries a discount_pct); source = 'live_fire_levels' before the
  // first snapshot, in which case discount_pct is null and the chip is
  // suppressed. Product art is NOT in the podium payload (rebuilding the
  // rendering, not the data layer) — it's fetched per handle from the
  // storefront's own /products/<handle>.js (same-origin, locale-safe).
  // Champion badges come from the existing /champions batch endpoint.
  // Empty slots stay hidden — fewer than 3 published designs is a valid
  // early-store state.

  // Flat brand medal (ribbon tails + metal disc + ink ring) — the one medal
  // drawing for every champion surface: card/PDP chip, Hall of Flame band,
  // Past Seasons cards. Tinted per rank via --vj-medal on the wrapper.
  var MEDAL_SVG =
    '<svg class="vj-medal" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<path class="vj-medal__ribbon" d="M7 1h4.6l-2.1 8.2-3.9-1.2L7 1z"/>' +
      '<path class="vj-medal__ribbon" d="M17 1h-4.6l2.1 8.2 3.9-1.2L17 1z"/>' +
      '<circle class="vj-medal__disc" cx="12" cy="14.8" r="6.4"/>' +
      '<circle class="vj-medal__ring" cx="12" cy="14.8" r="3.8"/>' +
    '</svg>';

  // Ask Shopify's CDN for a sized render so the podium doesn't pull full
  // product images. featured_image is "//cdn.shopify.com/...jpg?v=123".
  function sizedImg(url, w) {
    if (!url) return '';
    if (url.indexOf('//') === 0) url = 'https:' + url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  // Locale-aware product fetch for the Hall / Past Seasons cards. The backend
  // serves the CANONICAL default-locale handle + title; the localized
  // /<root>products/<handle>.js is what carries the Translate & Adapt title
  // the shopper sees on every other FR surface — but Shopify hard-404s the
  // localized .js when the FR handle itself is translated (the product-js
  // redirect trap), so on 404 we resolve the localized handle through the
  // HTML route's 301 (response.url) and retry the .js once — same self-heal
  // as vj-cart.js fetchProduct. Resolves { product, path } (path = the
  // locale-correct PDP path for links) or null for a dead product (callers
  // keep those cards hidden — QA-2026-07-01 M-1).
  function fetchLocalizedProduct(handle, root) {
    var path = ((root || '/') + 'products/' + handle).replace(/\/{2,}/g, '/');
    var jsFetch = function (p) {
      return fetch(p + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    };
    return jsFetch(path).then(function (p) {
      if (p) return { product: p, path: path };
      return fetch(path, { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) return null;
          if (r.body && typeof r.body.cancel === 'function') {
            try { r.body.cancel().catch(function () {}); } catch (_e) { /* noop */ }
          }
          var m = null;
          try { m = new URL(r.url, window.location.origin).pathname.match(/^(.*?\/products\/[^/?#]+)/); }
          catch (_e) { /* keep null */ }
          var resolved = m ? m[1] : null;
          if (!resolved || resolved === path) return null;
          return jsFetch(resolved).then(function (p2) {
            return p2 ? { product: p2, path: resolved } : null;
          });
        })
        .catch(function () { return null; });
    });
  }

  function initHallOfFlame() {
    var hall = document.querySelector('.vj-hall');
    if (!hall) return;
    var proxy = hall.dataset.proxy || '/apps/fire';
    var isFr = (document.documentElement.lang || '').toLowerCase().indexOf('fr') === 0;
    var vjRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(proxy + '/podium', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var rows = data && data.podium;
        if (!rows || !rows.length) return;

        // "Season N · refreshes in [month]" sublabel — month only, never a
        // specific date (owner call 2026-07-19: exact dates stay internal).
        // The month comes from season.refresh_month ('YYYY-MM', the owner's
        // declared season-turnover month via settings.season_refresh_month),
        // NOT next_snapshot_at — the biweekly podium snapshot is internal
        // mechanics, not the customer-facing refresh. When the key is unset
        // the label degrades to just "Season N" rather than guessing a month.
        // Parse Y/M by hand: new Date('2026-09') is UTC midnight, which
        // Intl-formats as the PREVIOUS month in western timezones.
        // Populated only when the backend reports an active season.
        var sub = hall.querySelector('[data-hall-sublabel]');
        if (sub && data.season && data.season.number) {
          var label = isFr ? 'Saison ' + data.season.number : 'Season ' + data.season.number;
          var rm = data.season.refresh_month;
          if (rm && /^\d{4}-\d{2}$/.test(rm)) {
            try {
              var d = new Date(parseInt(rm.slice(0, 4), 10), parseInt(rm.slice(5, 7), 10) - 1, 15);
              var fmt = new Intl.DateTimeFormat(isFr ? 'fr-CA' : 'en-CA', { month: 'long' });
              var refreshes = isFr ? 'rafraichit en ' + fmt.format(d) : 'refreshes in ' + fmt.format(d);
              label = label + ' · ' + refreshes;
            } catch (_e) { /* keep just Season N */ }
          }
          sub.textContent = label;
          sub.hidden = false;
        }

        var live = rows.slice(0, 3).filter(function (row) { return row && row.fire_level; });
        if (!live.length) return;

        // One champions batch for the (≤3) podium products, same shape as
        // the per-card path. Fire it in parallel; render badges when it lands.
        var pids = live.map(function (r) { return r.product_id; }).filter(Boolean);
        if (pids.length) {
          fetch(proxy + '/champions?v=2&product_ids=' + encodeURIComponent(pids.join(',')),
                { credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.json() : { champions: {} }; })
            .then(function (cj) {
              var champs = (cj && cj.champions) || {};
              live.forEach(function (row) {
                var list = champs[String(row.product_id)];
                if (!list || !list.length) return;
                var top = list.slice().sort(function (a, b) {
                  return (b.season_number || 0) - (a.season_number || 0);
                })[0];
                if (!top) return;
                var champEl = hall.querySelector('[data-hall-champ="' + (row.rank || 1) + '"]');
                if (!champEl) return;
                // Brand medal SVG + "S<n>" — same drawing as the card chip
                // (emoji medal retired 2026-07-14).
                champEl.innerHTML = MEDAL_SVG;
                var champTxt = document.createElement('span');
                champTxt.textContent = 'S' + top.season_number;
                champEl.appendChild(champTxt);
                champEl.classList.add('vj-hall__champ--rank' + top.final_rank);
                var t = isFr
                  ? 'Champion de la Saison ' + top.season_number
                  : 'Season ' + top.season_number + ' Champion';
                champEl.setAttribute('title', t);
                champEl.setAttribute('aria-label', t);
                champEl.hidden = false;
              });
            })
            .catch(function () { /* badges are non-essential */ });
        }

        var any = false;
        var reveal = function (cardEl) {
          cardEl.hidden = false;
          if (!any) { any = true; hall.hidden = false; }
        };
        live.forEach(function (row) {
          var rank = row.rank || 1;
          var cardEl  = hall.querySelector('[data-hall-card="' + rank + '"]');
          var titleEl = hall.querySelector('[data-hall-title="' + rank + '"]');
          var levelEl = hall.querySelector('[data-hall-level="' + rank + '"]');
          var linkEl  = hall.querySelector('[data-hall-link="' + rank + '"]');
          var pctEl   = hall.querySelector('[data-hall-pct="' + rank + '"]');
          var imgEl   = hall.querySelector('[data-hall-img="' + rank + '"]');
          if (!cardEl || !titleEl || !levelEl) return;

          var title = row.title || 'A Verre Jaune design';
          var populate = function () {
            titleEl.textContent = title;
            levelEl.textContent = formatCount(row.fire_level);
            if (row.handle && linkEl) {
              // Locale-prefix the product link so FR/US shoppers aren't dropped
              // to the default locale (QA-2026-07-01 M-5).
              linkEl.setAttribute('href', (vjRoot + 'products/' + row.handle).replace(/\/{2,}/g, '/'));
            }
            if (pctEl) {
              if (row.discount_pct != null) {
                pctEl.textContent = '−' + row.discount_pct + '%';
                pctEl.hidden = false;
              } else {
                pctEl.textContent = '';
                pctEl.hidden = true;
              }
            }
          };

          // No handle to verify (legacy/stranded row) → populate + reveal.
          if (!row.handle) { populate(); reveal(cardEl); return; }

          // Verify the product is actually live BEFORE revealing the card. A 404
          // (product unpublished/deleted — e.g. the Shopify-only-deletion sync
          // gap the backend liveness filter can't see) leaves the card hidden:
          // no broken card, no dead link, no console 404. QA-2026-07-01 M-1.
          // Locale-aware: the localized .js carries the FR Translate & Adapt
          // title on /fr-ca/ (owner rule 2026-07-19 — FR shoppers see FR
          // titles); its title + resolved path win over the API's canonical
          // default-locale values.
          if (imgEl) imgEl.alt = title;
          fetchLocalizedProduct(row.handle, vjRoot)
            .then(function (res) {
              if (!res) return; // dead product — leave the card hidden
              var p = res.product;
              if (p.title) { title = p.title; if (imgEl) imgEl.alt = p.title; }
              var src = p.featured_image || (p.images && p.images[0]);
              if (imgEl && src) imgEl.src = sizedImg(src, rank === 1 ? 720 : 520);
              populate();
              if (linkEl) linkEl.setAttribute('href', res.path);
              reveal(cardEl);
            });
        });
      })
      .catch(function () { /* leave hidden */ });
  }

  // ---- Past Seasons band (/pages/the-fire) ----
  // Fills [data-vj-past-seasons] from /apps/fire/history: one row per ended
  // season, its 3 champions as product cards (art fetched per handle from
  // /products/<handle>.js — same live-verify pattern as the Hall band; a
  // dead product's card is dropped, never a broken link). The whole band
  // stays hidden until at least one champion card lands, so a store with no
  // ended seasons shows nothing here.
  function initPastSeasons() {
    var root = document.querySelector('[data-vj-past-seasons]');
    if (!root) return;
    var rowsEl = root.querySelector('[data-vj-past-rows]');
    if (!rowsEl) return;
    var proxy = root.dataset.proxy || '/apps/fire';
    var isFr = (document.documentElement.lang || '').toLowerCase().indexOf('fr') === 0;
    var vjRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(proxy + '/history', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var seasons = data && data.seasons;
        if (!seasons || !seasons.length) return;
        seasons.forEach(function (s) {
          if (!s || !s.champions || !s.champions.length) return;
          var row = document.createElement('div');
          row.className = 'vj-tf__past-row';
          row.hidden = true;
          var head = document.createElement('p');
          head.className = 'vj-tf__past-season';
          var when = '';
          if (s.ended_at) {
            var d = new Date(s.ended_at);
            if (!isNaN(d)) {
              try {
                var fmt = new Intl.DateTimeFormat(isFr ? 'fr-CA' : 'en-CA', { month: 'long', year: 'numeric' });
                when = ' · ' + (isFr ? 'terminée ' : 'ended ') + fmt.format(d);
              } catch (_e) { /* keep just Season N */ }
            }
          }
          head.textContent = (isFr ? 'Saison ' : 'Season ') + s.number + when;
          row.appendChild(head);
          var grid = document.createElement('div');
          grid.className = 'vj-tf__past-grid';
          row.appendChild(grid);
          rowsEl.appendChild(row);
          s.champions.slice()
            .sort(function (a, b) { return (a.final_rank || 9) - (b.final_rank || 9); })
            .forEach(function (c) {
              if (!c || !c.handle) return;
              var card = document.createElement('a');
              card.className = 'vj-tf__past-card vj-tf__past-card--rank' + c.final_rank;
              card.href = (vjRoot + 'products/' + c.handle).replace(/\/{2,}/g, '/');
              card.hidden = true; // appended in rank order NOW, revealed on live-verify
              var media = document.createElement('span');
              media.className = 'vj-tf__past-media';
              var img = document.createElement('img');
              img.loading = 'lazy';
              img.decoding = 'async';
              img.alt = c.title || '';
              media.appendChild(img);
              var chip = document.createElement('span');
              chip.className = 'vj-tf__past-chip vj-fire__champion--rank' + c.final_rank;
              chip.innerHTML = MEDAL_SVG;
              var chipTxt = document.createElement('span');
              chipTxt.textContent = 'S' + s.number;
              chip.appendChild(chipTxt);
              var name = document.createElement('span');
              name.className = 'vj-tf__past-title';
              name.textContent = c.title || '';
              card.appendChild(media);
              card.appendChild(chip);
              card.appendChild(name);
              // Final flame count at season end — brand flame + Space Grotesk
              // tabular number, same visual language as the Hall band level.
              if (c.final_fire) {
                var fireLine = document.createElement('span');
                fireLine.className = 'vj-tf__past-fire';
                fireLine.innerHTML =
                  '<svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
                    '<path fill="#FF8A00" d="M16 2c1.6 4.2 6.4 6.2 6.4 12.1 0 4.1-2.1 6.6-2.1 6.6s1.2-3.4-1.6-5.6c.6 3.4-1 6.9-3.4 8.7-2-1.2-3.6-3.4-3.6-6.4 0-1.2.3-2 .3-2s-2 1.4-2 4.6c0 .5.1 1 .1 1S6 22.4 6 17.7C6 9.7 14.4 8.2 16 2z"/>' +
                  '</svg>';
                var fireN = document.createElement('span');
                fireN.textContent = formatCount(c.final_fire);
                fireLine.appendChild(fireN);
                fireLine.setAttribute('title', isFr ? 'Flamme finale de la Saison' : 'Final Season flame level');
                card.appendChild(fireLine);
              }
              grid.appendChild(card);
              // Locale-aware fetch: the localized .js title (FR Translate &
              // Adapt) wins over the API's canonical default-locale title.
              fetchLocalizedProduct(c.handle, vjRoot)
                .then(function (res) {
                  if (!res) { card.remove(); return; } // dead product — drop the card
                  var p = res.product;
                  var src = p.featured_image || (p.images && p.images[0]);
                  if (src) img.src = sizedImg(src, 520);
                  if (p.title) name.textContent = p.title;
                  img.alt = name.textContent;
                  card.href = res.path;
                  card.hidden = false;
                  row.hidden = false;
                  var firstReveal = root.hidden;
                  root.hidden = false;
                  // The band is [hidden] at document load, so a native
                  // #vj-past-seasons anchor jump (e.g. the homepage promo
                  // band's small link) finds nothing to scroll to — honor
                  // the hash manually on first reveal.
                  if (firstReveal && window.location.hash === '#' + root.id) {
                    try { root.scrollIntoView({ block: 'start' }); } catch (_e) {}
                  }
                })
                .catch(function () { card.remove(); });
            });
        });
      })
      .catch(function () { /* band stays hidden */ });
  }

  // ---- one-time-per-session first-visit nudge ----
  // The unlit flame has a transparent background (no pill chrome) and a low-
  // amplitude resting ember — both designed to read as "alive but uninflated."
  // The cost is that a first-time visitor may not register the flame as a tap
  // target. This nudge plays a single brighten + bob + tip-reveal on the first
  // in-viewport flame per browsing session. sessionStorage scope (not local)
  // so returning visitors get the discovery moment on a new tab, but not
  // repeatedly within one session.
  var NUDGE_KEY = 'vj_fire_nudged';
  function reducedMotion() {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
    catch (_) { return false; }
  }
  function initFirstVisitNudge() {
    if (reducedMotion()) return;
    try { if (sessionStorage.getItem(NUDGE_KEY) === '1') return; } catch (_) {}
    if (!('IntersectionObserver' in window)) return;
    var fired = false;
    var io = new IntersectionObserver(function (entries) {
      if (fired) { io.disconnect(); return; }
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (!entry.isIntersecting) continue;
        var el = entry.target;
        // Only nudge unlit flames — a lit one already has its yellow pill.
        if (el.classList.contains('is-lit')) continue;
        fired = true;
        try { sessionStorage.setItem(NUDGE_KEY, '1'); } catch (_) {}
        var isFr = (document.documentElement.lang || '').toLowerCase().indexOf('fr') === 0;
        var tipEl = el.querySelector('[data-fire-tip]');
        var prevHidden = tipEl ? tipEl.hidden : null;
        var prevText   = tipEl ? tipEl.textContent : null;
        if (tipEl) {
          tipEl.textContent = isFr ? 'Tape pour attiser' : 'Tap to add fire';
          tipEl.hidden = false;
        }
        el.classList.add('is-nudge-first', 'is-revealing-tip');
        setTimeout(function () {
          el.classList.remove('is-nudge-first', 'is-revealing-tip');
          if (tipEl) {
            tipEl.textContent = prevText || '';
            if (prevHidden !== null) tipEl.hidden = prevHidden;
          }
        }, 2000);
        io.disconnect();
        break;
      }
    }, { threshold: 0.5 });
    // Wait long enough for the /levels batch (fires +60ms after registration)
    // plus a typical network round-trip to land and set .is-lit on cards
    // that already cleared the display threshold. Otherwise we might nudge a
    // flame that is about to flip lit mid-animation.
    setTimeout(function () {
      document.querySelectorAll('.vj-fire').forEach(function (el) { io.observe(el); });
    }, 900);
  }

  // ---- boot ----
  function boot() {
    defineFireVote();
    initHallOfFlame();
    initPastSeasons();
    initFirstVisitNudge();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
